export type FetchErrorCategory =
  | 'cancelled'
  | 'deadline_exceeded'
  | 'network_error'
  | 'http_error'
  | 'schema_error'
  | 'authentication_required'
  | 'authorization_denied'
  | 'business_rejection'
  | 'duplicate_command'
  | 'configuration_error'
  | 'unknown'

export type RequestPolicy = {
  readonly id: string
  readonly kind: 'read' | 'mutation'
  readonly totalDeadlineMs: number
  readonly attemptTimeoutMs: number
  readonly maxAttempts: number
  readonly backoffBaseMs: number
  readonly maxBackoffMs: number
  readonly maxResponseBytes: number
  readonly retryableCategories: readonly FetchErrorCategory[]
  readonly retryableStatuses: readonly number[]
  readonly retryableMethods: readonly (
    'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  )[]
  readonly idempotency: 'forbidden' | 'required'
  readonly requiredCapabilities: readonly MutationCapability[]
}

export type MutationCapability =
  'authorization' | 'audit' | 'financial_commit_verification'

export const STANDARD_READ = definePolicy({
  id: 'standard-read-v1',
  kind: 'read',
  totalDeadlineMs: 15_000,
  attemptTimeoutMs: 5_000,
  maxAttempts: 3,
  backoffBaseMs: 150,
  maxBackoffMs: 1_500,
  maxResponseBytes: 1_048_576,
  retryableCategories: ['network_error', 'deadline_exceeded'],
  retryableStatuses: [408, 425, 429, 500, 502, 503, 504],
  retryableMethods: ['GET'],
  idempotency: 'forbidden',
  requiredCapabilities: [],
})

export const NO_RETRY_READ = definePolicy({
  id: 'no-retry-read-v1',
  kind: 'read',
  totalDeadlineMs: 15_000,
  attemptTimeoutMs: 15_000,
  maxAttempts: 1,
  backoffBaseMs: 0,
  maxBackoffMs: 0,
  maxResponseBytes: 1_048_576,
  retryableCategories: [],
  retryableStatuses: [],
  retryableMethods: ['GET'],
  idempotency: 'forbidden',
  requiredCapabilities: [],
})

export const USER_INITIATED_READ = definePolicy({
  id: 'user-initiated-read-v1',
  kind: 'read',
  totalDeadlineMs: 10_000,
  attemptTimeoutMs: 4_000,
  maxAttempts: 2,
  backoffBaseMs: 100,
  maxBackoffMs: 500,
  maxResponseBytes: 1_048_576,
  retryableCategories: ['network_error', 'deadline_exceeded'],
  retryableStatuses: [408, 425, 429, 500, 502, 503, 504],
  retryableMethods: ['GET'],
  idempotency: 'forbidden',
  requiredCapabilities: [],
})

export const BACKGROUND_READ = definePolicy({
  id: 'background-read-v1',
  kind: 'read',
  totalDeadlineMs: 30_000,
  attemptTimeoutMs: 8_000,
  maxAttempts: 4,
  backoffBaseMs: 250,
  maxBackoffMs: 3_000,
  maxResponseBytes: 1_048_576,
  retryableCategories: ['network_error', 'deadline_exceeded'],
  retryableStatuses: [408, 425, 429, 500, 502, 503, 504],
  retryableMethods: ['GET'],
  idempotency: 'forbidden',
  requiredCapabilities: [],
})

export const STANDARD_MUTATION = definePolicy({
  id: 'standard-mutation-v1',
  kind: 'mutation',
  totalDeadlineMs: 15_000,
  attemptTimeoutMs: 15_000,
  maxAttempts: 1,
  backoffBaseMs: 0,
  maxBackoffMs: 0,
  maxResponseBytes: 262_144,
  retryableCategories: [],
  retryableStatuses: [],
  retryableMethods: [],
  idempotency: 'forbidden',
  requiredCapabilities: [],
})

export const IDEMPOTENT_MUTATION = definePolicy({
  id: 'idempotent-mutation-v1',
  kind: 'mutation',
  totalDeadlineMs: 20_000,
  attemptTimeoutMs: 8_000,
  maxAttempts: 2,
  backoffBaseMs: 200,
  maxBackoffMs: 1_000,
  maxResponseBytes: 262_144,
  retryableCategories: ['network_error', 'deadline_exceeded'],
  retryableStatuses: [408, 425, 429, 500, 502, 503, 504],
  retryableMethods: ['POST', 'PUT', 'PATCH', 'DELETE'],
  idempotency: 'required',
  requiredCapabilities: [],
})

export const PRIVILEGED_MUTATION = definePolicy({
  id: 'privileged-mutation-v1',
  kind: 'mutation',
  totalDeadlineMs: 15_000,
  attemptTimeoutMs: 15_000,
  maxAttempts: 1,
  backoffBaseMs: 0,
  maxBackoffMs: 0,
  maxResponseBytes: 262_144,
  retryableCategories: [],
  retryableStatuses: [],
  retryableMethods: [],
  idempotency: 'required',
  requiredCapabilities: ['authorization', 'audit'],
})

export const FINANCIAL_MUTATION = definePolicy({
  id: 'financial-mutation-v1',
  kind: 'mutation',
  totalDeadlineMs: 15_000,
  attemptTimeoutMs: 15_000,
  maxAttempts: 1,
  backoffBaseMs: 0,
  maxBackoffMs: 0,
  maxResponseBytes: 262_144,
  retryableCategories: [],
  retryableStatuses: [],
  retryableMethods: [],
  idempotency: 'required',
  requiredCapabilities: [
    'authorization',
    'audit',
    'financial_commit_verification',
  ],
})

function definePolicy(policy: RequestPolicy): RequestPolicy {
  return Object.freeze({
    ...policy,
    retryableCategories: Object.freeze([...policy.retryableCategories]),
    retryableStatuses: Object.freeze([...policy.retryableStatuses]),
    retryableMethods: Object.freeze([...policy.retryableMethods]),
    requiredCapabilities: Object.freeze([...policy.requiredCapabilities]),
  })
}
